/**
 * Phase 1b · Slice 2c — Passive attribution capture.
 *
 * Runs once per session on public entry pages. Captures ALLOWLISTED
 * UTM/click-id parameters + landing_path + referring domain (host only).
 * Persists first-touch and last-touch separately in localStorage.
 *
 * Attribution failure MUST NEVER block checkout — every call is wrapped
 * in try/catch and returns silently.
 */
export const CAPTURE_VERSION = "2c-1";

const FT_KEY = "cu.attr.first_touch.v1";
const LT_KEY = "cu.attr.last_touch.v1";

const ALLOWED_UTM_KEYS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
  "gclid", "fbclid",
];

const SENSITIVE_KEYS = new Set([
  "email", "phone", "name", "address", "claim_code", "claim",
  "token", "order_token", "private_url", "auth", "session",
  "password", "pw",
]);

/** Read allowlisted query params + landing path + referring host,
 * dropping any sensitive keys entirely. Returns `null` if nothing
 * useful was captured. */
export function readAttributionFromLocation(location = window.location,
                                              document_ = document) {
  try {
    const url = new URL(location.href);
    const out = {};
    let touchedSomething = false;
    let sensitiveDropped = false;
    for (const [k, v] of url.searchParams.entries()) {
      const kl = k.toLowerCase();
      if (SENSITIVE_KEYS.has(kl)) { sensitiveDropped = true; continue; }
      if (ALLOWED_UTM_KEYS.includes(kl)) {
        out[kl] = String(v).slice(0, 200);
        touchedSomething = true;
      }
    }
    // Always capture the landing path (privacy-safe — path only, no query).
    out.landing_path = url.pathname;
    // Referring host (no path, no query — sanitized in `sales.attribution`).
    if (document_?.referrer) {
      try {
        const rurl = new URL(document_.referrer);
        if (rurl.hostname && rurl.hostname !== url.hostname) {
          out.referring_domain = rurl.hostname;
          touchedSomething = true;
        }
      } catch (_) {}
    }
    if (!touchedSomething) return null;
    if (sensitiveDropped) out._sensitive_dropped = true;
    return out;
  } catch (_) {
    return null;
  }
}

function _safeParse(key) {
  try { return JSON.parse(window.localStorage.getItem(key) || "null"); }
  catch (_) { return null; }
}
function _safeSet(key, value) {
  try { window.localStorage.setItem(key, JSON.stringify(value)); }
  catch (_) { /* private mode / quota — ignore silently */ }
}

/** Persist first-touch (write once) and last-touch (update when the new
 * payload has a real utm_source or referring_domain). */
export function persistAttribution(payload) {
  if (!payload) return { first: _safeParse(FT_KEY), last: _safeParse(LT_KEY) };
  const now = new Date().toISOString();
  const withAt = { ...payload, at: now };
  const first = _safeParse(FT_KEY);
  if (!first) _safeSet(FT_KEY, withAt);
  // Last-touch updates whenever payload has any UTM key or referring_domain.
  const hasReal = ALLOWED_UTM_KEYS.some(k => payload[k]) || payload.referring_domain;
  if (hasReal) _safeSet(LT_KEY, withAt);
  return { first: _safeParse(FT_KEY), last: _safeParse(LT_KEY) };
}

/** Fire-and-forget capture to backend. Never throws. */
export async function sendAttributionForOrder({ apiClient, source_type, source_order_id }) {
  try {
    const first = _safeParse(FT_KEY);
    const last  = _safeParse(LT_KEY);
    // Prefer last-touch (most recent), fall back to first-touch.
    const payload = last || first;
    if (!payload) return { stored: false, reason: "no_attribution_available" };
    const body = {
      source_type, source_order_id,
      utm_source: payload.utm_source, utm_medium: payload.utm_medium,
      utm_campaign: payload.utm_campaign, utm_content: payload.utm_content,
      utm_term: payload.utm_term,
      gclid: payload.gclid, fbclid: payload.fbclid,
      landing_path: payload.landing_path,
      referring_domain: payload.referring_domain,
    };
    const r = await apiClient.post("/admin/sales/attribution/capture", body);
    return r.data;
  } catch (_) {
    return { stored: false, reason: "attribution_error" };
  }
}
